import { Link } from "react-router-dom";
import { ArrowRight, ArrowDownToLine, Send, Wallet, Repeat, Link2, Code2, Check } from "lucide-react";
import Reveal from "../components/Reveal";
import CtaSection from "../components/CtaSection";
import FxConverter from "../components/FxConverter";

const sections = [
  {
    id: "payments",
    icon: ArrowDownToLine,
    eyebrow: "Payments",
    title: "Accept and manage incoming payments.",
    desc: "Give your customers simple ways to pay you, and keep every incoming payment visible from one workspace.",
    features: ["Payment collection", "Checkout", "Payment links", "Bank transfers", "Payment notifications", "Transaction tracking"],
  },
  {
    id: "payouts",
    icon: Send,
    eyebrow: "Payouts",
    title: "Send money to customers, suppliers, partners and beneficiaries.",
    desc: "Move funds out to the people and businesses you work with, individually or at scale, across supported markets.",
    features: ["Individual payouts", "Bulk payouts", "Domestic payouts", "International payouts", "Beneficiary management", "Payout tracking"],
  },
  {
    id: "accounts",
    icon: Wallet,
    eyebrow: "Multi-Currency Accounts",
    title: "Manage multiple supported currencies.",
    desc: "Hold, receive and send funds across currencies with a clear view of every balance and every movement.",
    features: ["Currency balances", "Account details", "Incoming funds", "Outgoing funds", "Transaction history", "Statements"],
  },
  {
    id: "fx",
    icon: Repeat,
    eyebrow: "FX",
    title: "Move between currencies.",
    desc: "Convert between supported currencies as a natural part of your money movement workflow — with clarity on every conversion.",
    features: ["Currency conversion", "FX transaction tracking", "Multi-currency management", "Transparent transaction visibility"],
  },
  {
    id: "payment-links",
    icon: Link2,
    eyebrow: "Payment Links",
    title: "Create payment experiences without building a full checkout.",
    desc: "Generate a secure payment link, share it through any digital channel, and get paid — no custom checkout build required.",
    features: ["Secure link creation", "Share via any digital channel", "No custom checkout required", "Payment status tracking"],
  },
  {
    id: "apis",
    icon: Code2,
    eyebrow: "APIs",
    title: "Build Swiftwire capabilities directly into your own platform.",
    desc: "Designed for developers: modern APIs to power collections, payouts, accounts and FX from inside your product.",
    features: ["REST API design", "Secure API access", "Webhooks for status updates", "Sandbox environment"],
  },
];

const Products = () => (
  <div data-testid="products-page">
    <section className="sw-grid-bg border-b border-[#E2E8F0] bg-white py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2E7CD6]">Products</p>
          <h1 className="mt-4 font-heading text-4xl font-extrabold tracking-tight text-[#0F172A] sm:text-5xl lg:text-6xl">
            Payment infrastructure built around your business.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#334155] md:text-lg">
            From collections and payouts to accounts, FX and APIs, Swiftwire gives you the tools to move money across Africa and beyond.
          </p>
        </Reveal>
      </div>
    </section>

    {sections.map((s, i) => (
      <section
        key={s.id}
        id={s.id}
        data-testid={`product-section-${s.id}`}
        className={`scroll-mt-24 py-16 md:py-24 ${i % 2 === 1 ? "bg-[#F8FAFC]" : "bg-white"}`}
      >
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
          <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#EFF5FD] text-[#1E3A6E]">
              <s.icon size={22} strokeWidth={1.5} />
            </span>
            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.18em] text-[#2E7CD6]">{s.eyebrow}</p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-[#0F172A] md:text-4xl">{s.title}</h2>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-[#334155]">{s.desc}</p>
            {s.id === "fx" && <FxConverter />}
            <Link
              to="/contact"
              data-testid={`product-${s.id}-cta`}
              className="group mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3A6E]"
            >
              Talk to us about {s.eyebrow}
              <ArrowRight size={15} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.12} className={i % 2 === 1 ? "lg:order-1" : ""}>
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm sm:p-8">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Capabilities</p>
              <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                {s.features.map((f) => (
                  <li key={f} className="flex items-center gap-2.5 rounded-lg border border-[#F1F5F9] bg-[#F8FAFC] px-3.5 py-3 text-sm font-medium text-[#334155]">
                    <Check size={15} strokeWidth={2.5} className="shrink-0 text-emerald-500" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>
    ))}

    <CtaSection title="Bring your money movement together." subtitle="One platform for collections, payouts, accounts, FX and APIs." />
  </div>
);

export default Products;
